import { useState, useEffect, useCallback } from 'react'
import api from '../lib/api'

const TIPO_ERROR_LABEL = {
  RECHAZADO: { label: 'Rechazado MH', color: '#e74c3c' },
  HTTP_ERROR: { label: 'Error HTTP',   color: '#e67e22' },
  EXCEPCION:  { label: 'Excepción',    color: '#8e44ad' },
}

const TIPO_DTE_LABEL = {
  '01': 'FCF', '03': 'CCF', '05': 'NC', '11': 'FEX', '14': 'FES',
}

export default function Logs() {
  const [logs,    setLogs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [page,    setPage]    = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total,   setTotal]   = useState(0)
  const [expanded, setExpanded] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [filters, setFilters] = useState({
    tipo_error: '', tipo_dte: '', desde: '', hasta: '', q: '',
  })

  const cargar = useCallback((p = 1) => {
    setLoading(true)
    const params = { page: p, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '')) }
    api.get('/system-logs', { params })
      .then(({ data }) => {
        setLogs(data.data)
        setPage(data.current_page)
        setLastPage(data.last_page)
        setTotal(data.total)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filters])

  useEffect(() => { cargar(1) }, [cargar])

  function applyFilters(e) {
    e.preventDefault()
    cargar(1)
  }

  function clearFilters() {
    setFilters({ tipo_error: '', tipo_dte: '', desde: '', hasta: '', q: '' })
  }

  async function deleteOne(id) {
    if (!confirm('¿Eliminar este log?')) return
    await api.delete(`/system-logs/${id}`)
    setLogs(prev => prev.filter(l => l.id !== id))
    setTotal(t => t - 1)
  }

  async function deleteAll() {
    if (!confirm(`¿Eliminar todos los ${total} registros mostrados?`)) return
    setDeleting(true)
    try {
      await api.delete('/system-logs/all')
      cargar(1)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="content">
      <div className="content-header">
        <h1 className="content-title">Logs del sistema</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <span style={{ color: 'var(--muted)', fontSize: '13px', alignSelf: 'center' }}>
            {total} registro{total !== 1 ? 's' : ''}
          </span>
          {total > 0 && (
            <button className="btn btn-sm btn-danger" onClick={deleteAll} disabled={deleting}>
              {deleting ? 'Eliminando…' : 'Limpiar todo'}
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={applyFilters} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', alignItems: 'flex-end' }}>
        <input
          className="input"
          placeholder="Buscar código, control, tenant, descripción…"
          value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          style={{ flex: '1 1 220px', minWidth: '180px' }}
        />
        <select className="input" value={filters.tipo_error} onChange={e => setFilters(f => ({ ...f, tipo_error: e.target.value }))} style={{ flex: '0 0 140px' }}>
          <option value="">Todos los errores</option>
          <option value="RECHAZADO">Rechazado MH</option>
          <option value="HTTP_ERROR">Error HTTP</option>
          <option value="EXCEPCION">Excepción</option>
        </select>
        <select className="input" value={filters.tipo_dte} onChange={e => setFilters(f => ({ ...f, tipo_dte: e.target.value }))} style={{ flex: '0 0 110px' }}>
          <option value="">Todos los DTE</option>
          <option value="01">FCF (01)</option>
          <option value="03">CCF (03)</option>
          <option value="05">NC  (05)</option>
          <option value="11">FEX (11)</option>
          <option value="14">FES (14)</option>
        </select>
        <input type="date" className="input" value={filters.desde} onChange={e => setFilters(f => ({ ...f, desde: e.target.value }))} style={{ flex: '0 0 130px' }} />
        <input type="date" className="input" value={filters.hasta} onChange={e => setFilters(f => ({ ...f, hasta: e.target.value }))} style={{ flex: '0 0 130px' }} />
        <button type="submit" className="btn btn-primary btn-sm">Filtrar</button>
        <button type="button" className="btn btn-sm" onClick={clearFilters}>Limpiar</button>
      </form>

      {loading && <div style={{ color: 'var(--muted)' }}>Cargando…</div>}

      {!loading && logs.length === 0 && (
        <div className="empty">No hay logs de error registrados.</div>
      )}

      {!loading && logs.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>DTE</th>
                  <th>Tipo error</th>
                  <th>HTTP</th>
                  <th>Código MH</th>
                  <th>Descripción</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const tipoInfo = TIPO_ERROR_LABEL[log.tipo_error] ?? { label: log.tipo_error, color: '#999' }
                  const isOpen = expanded === log.id
                  return (
                    <>
                      <tr
                        key={log.id}
                        style={{ cursor: 'pointer', background: isOpen ? 'var(--hover)' : undefined }}
                        onClick={() => setExpanded(isOpen ? null : log.id)}
                      >
                        <td style={{ whiteSpace: 'nowrap', fontSize: '12px', color: 'var(--muted)' }}>
                          {new Date(log.created_at).toLocaleString('es-SV')}
                        </td>
                        <td>
                          <span style={{ fontWeight: 600 }}>{log.tenant_name ?? '—'}</span>
                          {log.tenant_subdomain && <span style={{ color: 'var(--muted)', fontSize: '11px', marginLeft: '4px' }}>({log.tenant_subdomain})</span>}
                        </td>
                        <td>
                          {log.tipo_dte
                            ? <span className="badge badge-neutral">{TIPO_DTE_LABEL[log.tipo_dte] ?? log.tipo_dte}</span>
                            : '—'}
                        </td>
                        <td>
                          <span className="badge" style={{ background: tipoInfo.color, color: '#fff' }}>
                            {tipoInfo.label}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px' }}>{log.http_status ?? '—'}</td>
                        <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{log.mh_codigo_msg ?? '—'}</td>
                        <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }}>
                          {log.mh_descripcion_msg ?? log.exception_message ?? '—'}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <button className="btn btn-sm btn-danger" onClick={() => deleteOne(log.id)} style={{ padding: '2px 8px' }}>×</button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr key={`${log.id}-detail`}>
                          <td colSpan={8} style={{ background: 'var(--surface-2, #f9f9f9)', padding: '16px 20px' }}>
                            <LogDetail log={log} />
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {lastPage > 1 && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '12px', justifyContent: 'center' }}>
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => cargar(page - 1)}>‹ Anterior</button>
              <span style={{ alignSelf: 'center', fontSize: '13px', color: 'var(--muted)' }}>Página {page} / {lastPage}</span>
              <button className="btn btn-sm" disabled={page >= lastPage} onClick={() => cargar(page + 1)}>Siguiente ›</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function LogDetail({ log }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '12px' }}>
      {/* Columna izquierda: info general */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Section title="Identificación del documento">
          <Field label="Código generación" value={log.codigo_generacion} mono />
          <Field label="Número de control"  value={log.numero_control}   mono />
          <Field label="Tipo DTE"           value={log.tipo_dte ? `${log.tipo_dte} — ${TIPO_DTE_LABEL[log.tipo_dte] ?? ''}` : null} />
        </Section>

        <Section title="Respuesta del Ministerio de Hacienda">
          <Field label="Estado MH"       value={log.mh_estado} />
          <Field label="Código msg"      value={log.mh_codigo_msg}      mono />
          <Field label="Descripción"     value={log.mh_descripcion_msg} />
          {log.mh_observaciones?.length > 0 && (
            <div>
              <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Observaciones:</span>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {log.mh_observaciones.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
        </Section>

        {(log.exception_message || log.exception_trace) && (
          <Section title="Excepción PHP">
            <Field label="Mensaje" value={log.exception_message} />
            {log.exception_trace && (
              <pre style={{ margin: '4px 0 0', padding: '8px', background: '#1e1e1e', color: '#d4d4d4', borderRadius: '4px', overflowX: 'auto', fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                {log.exception_trace}
              </pre>
            )}
          </Section>
        )}
      </div>

      {/* Columna derecha: payloads JSON */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {log.request_payload && (
          <Section title="Payload enviado a MH">
            <JsonBlock data={log.request_payload} />
          </Section>
        )}
        {log.mh_response && (
          <Section title="Respuesta completa MH">
            <JsonBlock data={log.mh_response} />
          </Section>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '10px 12px' }}>
      <div style={{ fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '8px', letterSpacing: '0.5px' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>{children}</div>
    </div>
  )
}

function Field({ label, value, mono = false }) {
  if (!value) return null
  return (
    <div style={{ display: 'flex', gap: '6px' }}>
      <span style={{ color: 'var(--muted)', minWidth: '120px', flexShrink: 0 }}>{label}:</span>
      <span style={mono ? { fontFamily: 'monospace', wordBreak: 'break-all' } : { wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

function JsonBlock({ data }) {
  return (
    <pre style={{ margin: 0, padding: '8px', background: '#1e1e1e', color: '#9cdcfe', borderRadius: '4px', overflowX: 'auto', fontSize: '11px', whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto' }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}
